// Long-form legal copy for the Terms of Service / Privacy Policy pages, kept as
// structured data (rather than the i18n bundles) so the parity test isn't forced
// to cover thousands of words. This is the offline/prerender FALLBACK — the live
// pages render the CMS copy (GET /api/v1/content/{slug}), which is kept in sync
// via backend data migrations. English/Ukrainian Terms come from the owner's
// "Terms of Service.docx" (14 July 2026 revision, adds the account-deletion
// section required by Facebook); German is a translation of that source and
// should be reviewed by legal/native review.

export interface LegalSection {
  /** Stable, locale-independent anchor id (e.g. /terms#account-deletion). */
  id: string;
  heading: string;
  paragraphs: string[];
  /** Optional bullet list rendered after the paragraphs. */
  bullets?: string[];
  /** Optional numbered step list rendered after the paragraphs. */
  steps?: string[];
  /** Optional paragraphs rendered after the bullet/step list. */
  paragraphsAfter?: string[];
  /** Optional contact block rendered after the paragraphs (bold name + lines). */
  contact?: { name: string; lines: string[] };
}

export interface LegalDoc {
  /** Page H1, localized with the body. */
  title: string;
  /** "Last updated" date, shown under the title. */
  updated: string;
  sections: LegalSection[];
}

export type LegalLocale = 'en' | 'uk' | 'de';

/** Standalone Contacts page content (no "last updated" / sections). */
export interface ContactPageContent {
  /** Page H1. */
  title: string;
  /** Short lead paragraph under the title. */
  intro: string;
  /** Legal entity name. */
  orgName: string;
  /** State registration line (ЄДРПОУ / registration ID). */
  registration: string;
  /** Postal address. */
  address: string;
  /** Contact email (rendered as a mailto link). */
  email: string;
  /** Contact phone (rendered as a tel link). */
  phone: string;
}

export const TERMS: Record<LegalLocale, LegalDoc> = {
  en: {
    title: 'Terms of Service',
    updated: '14 July 2026',
    sections: [
      {
        id: 'about-the-service',
        heading: 'About the Service',
        paragraphs: [
          'Streamko is a subscription-based online service that provides background music streaming and audio management tools for business environments such as cafés, restaurants, retail stores, salons, offices, and other commercial spaces.',
          'The service allows users to play licensed background music, create playback scenarios, manage playlists, and schedule audio announcements and promotional messages through a web-based interface.',
          'Streamko is operated by Eva Media LLC, Kyiv, Ukraine.',
        ],
      },
      {
        id: 'user-accounts',
        heading: 'User Accounts',
        paragraphs: [
          'To use certain features of the service, users may be required to create an account.',
          'Users are responsible for maintaining the confidentiality of their account credentials and for all activities that occur under their account.',
          'Streamko reserves the right to suspend or terminate accounts that violate these Terms or misuse the service.',
        ],
      },
      {
        id: 'subscription-and-billing',
        heading: 'Subscription and Billing',
        paragraphs: [
          'Streamko operates on a subscription model.',
          'Access to certain features of the service may require an active paid subscription. Subscription plans and pricing may vary depending on the features provided.',
          'Subscriptions may be billed on a recurring monthly basis.',
          'Payments for subscriptions may be processed by third-party payment providers.',
          'Users may cancel their subscription at any time through their account settings. Cancellation will take effect at the end of the current billing period, and the service will remain available until that time.',
        ],
      },
      {
        id: 'service-delivery',
        heading: 'Service Delivery, Payment Methods and Access',
        paragraphs: [
          'Streamko is a digital subscription service. No physical goods are delivered.',
          'Access to the service is provided electronically through a web-based interface after successful registration and activation of the relevant subscription plan.',
          'Free trial access, where available, is activated immediately after registration.',
          'Paid subscriptions are activated after successful payment confirmation.',
          'The service is available to users during the active subscription period.',
          'The following payment methods may be available:',
        ],
        bullets: [
          'Payment cards processed through WayForPay.',
          'Other payment methods supported by the payment provider and displayed during checkout.',
        ],
        paragraphsAfter: [
          'Subscription plans, billing periods, and applicable fees are displayed on the Pricing page of the website.',
        ],
      },
      {
        id: 'refund-policy',
        heading: 'Refund Policy and Cancellation',
        paragraphs: [
          'Users may cancel the automatic renewal of their subscription at any time through their account settings.',
          'Cancellation of a monthly subscription takes effect at the end of the current paid billing period. Payments for an active monthly billing period are non-refundable, unless otherwise required by applicable law.',
          'For annual subscriptions, users may request a partial refund for the unused portion of the subscription period.',
          'The refund amount is calculated based on the applicable monthly subscription rate for the period already used and is not determined by dividing the annual subscription fee by twelve.',
          'For example, if a user purchases an annual subscription and requests cancellation after three months of use, Streamko may deduct the cost of three months at the applicable monthly subscription rate. The remaining balance may be refunded to the user.',
          'Refund requests are reviewed individually. To request a refund, users must contact Streamko at info@streamko.pro and provide details of the relevant transaction.',
          'Requests for refunds under annual subscriptions must be submitted within 30 calendar days from the effective date of cancellation of the subscription. Requests submitted after this period may not be eligible for a refund.',
          'Approved refunds will be processed using the original payment method within a reasonable period of time and in accordance with the policies of the applicable payment provider.',
        ],
      },
      {
        id: 'account-deletion',
        heading: 'Account and User Data Deletion',
        paragraphs: [
          'The User may delete their Streamko account and the personal data associated with it at any time.',
          'To do so, the User must:',
        ],
        steps: [
          'sign in to their Streamko account;',
          'open the Profile section;',
          'tap Delete Account;',
          'confirm the deletion.',
        ],
        paragraphsAfter: [
          'After confirmation, the account will be deleted, and the related personal data will be deleted or anonymized, except for information that Streamko is required to retain for a specified period under applicable law, including for tax, accounting, contractual, or other legal obligations.',
          'Account deletion is irreversible and may result in the loss of access to settings, created scenarios, playlists, usage history, and other account-related data.',
        ],
      },
      {
        id: 'intellectual-property',
        heading: 'Intellectual Property',
        paragraphs: [
          'All content available through the Streamko service, including music, playlists, audio materials, and software components, is protected by intellectual property laws.',
          'Users are granted a limited, non-exclusive, non-transferable right to access and use the service for their business purposes in accordance with these Terms.',
          'Users may not reproduce, redistribute, or otherwise exploit any content provided through the service outside the functionality of the platform.',
        ],
      },
      {
        id: 'service-availability',
        heading: 'Availability of the Service',
        paragraphs: [
          'Streamko strives to provide reliable and continuous access to the service. However, the service may occasionally be unavailable due to maintenance, updates, or technical issues.',
          'The service is provided on an “as is” and “as available” basis.',
        ],
      },
      {
        id: 'limitation-of-liability',
        heading: 'Limitation of Liability',
        paragraphs: [
          'To the maximum extent permitted by applicable law, Streamko, operated by Eva Media LLC, shall not be liable for any indirect, incidental, or consequential damages resulting from the use or inability to use the service.',
        ],
      },
      {
        id: 'changes-to-terms',
        heading: 'Changes to the Service or Terms',
        paragraphs: [
          'Streamko may update or modify these Terms from time to time. Updated versions will be published on this page.',
          'Continued use of the service after such changes constitutes acceptance of the updated Terms.',
        ],
      },
      {
        id: 'contact',
        heading: 'Contact Information',
        paragraphs: [
          'If you have any questions about these Terms, you may contact us at:',
        ],
        contact: {
          name: 'Eva Media LLC',
          lines: [
            'State Registration ID No. 42394828',
            'Pryrichna Str., 19B, #83, 04213 Kyiv, Ukraine',
            'Email: info@streamko.pro',
          ],
        },
      },
    ],
  },
  uk: {
    title: 'Умови використання (Публічна оферта)',
    updated: '14 липня 2026',
    sections: [
      {
        id: 'about-the-service',
        heading: 'Про сервіс',
        paragraphs: [
          'Streamko — це онлайн-сервіс, що працює за моделлю підписки та призначений для відтворення фонової музики і керування аудіоконтентом у комерційних закладах, таких як кафе, ресторани, магазини, салони краси, офіси та інші бізнес-простори.',
          'Сервіс дозволяє користувачам відтворювати ліцензовану фонову музику, створювати сценарії відтворення, керувати плейлистами, а також планувати відтворення рекламних повідомлень та інших аудіооголошень через вебінтерфейс.',
          'Сервіс Streamko належить та управляється ТОВ «ЄВА МЕДІА», м. Київ, Україна.',
        ],
      },
      {
        id: 'user-accounts',
        heading: 'Обліковий запис користувача',
        paragraphs: [
          'Для використання окремих можливостей сервісу користувачу необхідно створити обліковий запис.',
          'Користувач несе відповідальність за збереження конфіденційності даних для входу до свого облікового запису та за всі дії, що здійснюються з використанням такого облікового запису.',
          'Streamko залишає за собою право тимчасово обмежити доступ або припинити використання облікового запису у разі порушення цих Умов використання або неправомірного використання сервісу.',
        ],
      },
      {
        id: 'subscription-and-billing',
        heading: 'Підписка та оплата',
        paragraphs: [
          'Streamko надається за моделлю підписки.',
          'Для доступу до окремих функцій сервісу може знадобитися активна платна підписка. Вартість підписки та доступні тарифні плани можуть відрізнятися залежно від обраного набору функцій.',
          'Оплата підписки може здійснюватися на щомісячній або щорічній основі.',
          'Платежі можуть оброблятися сторонніми платіжними провайдерами.',
          'Користувач може скасувати автоматичне поновлення підписки у будь-який час через налаштування свого облікового запису.',
          'Скасування набирає чинності після завершення поточного оплаченого розрахункового періоду. До цього моменту користувач зберігає доступ до сервісу.',
        ],
      },
      {
        id: 'service-delivery',
        heading: 'Надання сервісу, способи оплати та доступ',
        paragraphs: [
          'Streamko є цифровим сервісом за підпискою. Жодні матеріальні товари не постачаються.',
          'Доступ до сервісу надається в електронній формі через вебінтерфейс після успішної реєстрації користувача та активації відповідного тарифного плану.',
          'Якщо передбачено пробний період, доступ до нього надається одразу після реєстрації.',
          'Платна підписка активується після підтвердження успішної оплати.',
          'Користувач має доступ до сервісу протягом строку дії активної підписки.',
          'Доступні способи оплати можуть включати:',
        ],
        bullets: [
          'Банківські картки через WayForPay.',
          'Інші способи оплати, які підтримуються платіжним провайдером та доступні під час оформлення підписки.',
        ],
        paragraphsAfter: [
          'Інформація про тарифні плани, вартість підписки та розрахункові періоди розміщується на сторінці «Тарифи».',
        ],
      },
      {
        id: 'refund-policy',
        heading: 'Скасування підписки та повернення коштів',
        paragraphs: [
          'Користувач може у будь-який час скасувати автоматичне поновлення підписки через налаштування свого облікового запису.',
          'У разі щомісячної підписки скасування набирає чинності після завершення поточного оплаченого періоду. Кошти за вже оплачений місяць не повертаються, якщо інше не передбачено чинним законодавством.',
          'Для річної підписки користувач може звернутися із запитом щодо часткового повернення коштів за невикористаний період.',
          'Розмір повернення визначається виходячи з вартості фактично використаного періоду за відповідним місячним тарифом, а не шляхом простого поділу вартості річної підписки на дванадцять місяців.',
          'Наприклад, якщо користувач придбав річну підписку та звернувся із запитом про її скасування після трьох місяців використання, Streamko може утримати вартість трьох місяців за відповідним місячним тарифом, а залишок коштів повернути користувачу.',
          'Кожен запит на повернення коштів розглядається індивідуально.',
          'Для оформлення запиту необхідно звернутися за адресою info@streamko.pro та надати інформацію про відповідну оплату.',
          'Запити щодо повернення коштів за річною підпискою повинні бути подані не пізніше ніж через 30 календарних днів після дати набрання чинності скасування підписки.',
          'Повернення коштів, якщо воно погоджене, здійснюється тим самим способом оплати, яким була проведена оплата, протягом розумного строку відповідно до правил відповідного платіжного провайдера.',
        ],
      },
      {
        id: 'account-deletion',
        heading: 'Видалення облікового запису та даних користувача',
        paragraphs: [
          'Користувач може в будь-який момент видалити свій обліковий запис Streamko та пов’язані з ним персональні дані.',
          'Для цього необхідно:',
        ],
        steps: [
          'увійти до облікового запису Streamko;',
          'відкрити розділ меню «Профіль»;',
          'натиснути «Видалити акаунт»;',
          'підтвердити видалення.',
        ],
        paragraphsAfter: [
          'Після підтвердження обліковий запис буде видалено, а пов’язані з ним персональні дані — видалено або знеособлено, крім інформації, яку Streamko зобов’язаний зберігати протягом установленого строку відповідно до вимог законодавства, зокрема для виконання податкових, бухгалтерських, договірних або інших юридичних обов’язків.',
          'Видалення облікового запису є незворотним і може призвести до втрати доступу до налаштувань, створених сценаріїв, плейлистів, історії використання та інших даних облікового запису.',
        ],
      },
      {
        id: 'intellectual-property',
        heading: 'Інтелектуальна власність',
        paragraphs: [
          'Усі матеріали, доступні через сервіс Streamko, включаючи музичні твори, плейлисти, аудіоматеріали, програмне забезпечення та інші елементи сервісу, є об’єктами права інтелектуальної власності та охороняються законодавством.',
          'Користувачу надається обмежене, невиключне та непередаване право користування сервісом виключно для власних потреб у межах функціональності Streamko та відповідно до цих Умов використання.',
          'Користувач не має права копіювати, поширювати, відтворювати або іншим способом використовувати будь-які матеріали сервісу поза межами його функціональних можливостей.',
        ],
      },
      {
        id: 'service-availability',
        heading: 'Доступність сервісу',
        paragraphs: [
          'Streamko докладає розумних зусиль для забезпечення безперервної та стабільної роботи сервісу.',
          'Водночас сервіс може бути тимчасово недоступним у зв’язку з проведенням технічного обслуговування, оновленням програмного забезпечення або виникненням технічних несправностей.',
          'Сервіс надається за принципом «як є» (as is) та «за наявності» (as available).',
        ],
      },
      {
        id: 'limitation-of-liability',
        heading: 'Обмеження відповідальності',
        paragraphs: [
          'У межах, дозволених чинним законодавством, Streamko та ТОВ «ЄВА МЕДІА» не несуть відповідальності за будь-які непрямі, випадкові або опосередковані збитки, що виникли внаслідок використання або неможливості використання сервісу.',
        ],
      },
      {
        id: 'changes-to-terms',
        heading: 'Зміни до сервісу та Умов використання',
        paragraphs: [
          'Streamko може періодично змінювати або оновлювати ці Умови використання.',
          'Нова редакція документа публікується на цій сторінці із зазначенням дати останнього оновлення.',
          'Подальше використання сервісу після набрання чинності таких змін означає згоду користувача з оновленою редакцією Умов використання.',
        ],
      },
      {
        id: 'contact',
        heading: 'Контактна інформація',
        paragraphs: [
          'Якщо у вас виникли запитання щодо цих Умов використання, ви можете звернутися до нас:',
        ],
        contact: {
          name: 'ТОВ «ЄВА МЕДІА»',
          lines: [
            'Код ЄДРПОУ: 42394828',
            'вул. Прирічна, 19Б, офіс 83, м. Київ, 04213, Україна',
            'Email: info@streamko.pro',
          ],
        },
      },
    ],
  },
  de: {
    title: 'Nutzungsbedingungen',
    updated: '14. Juli 2026',
    sections: [
      {
        id: 'about-the-service',
        heading: 'Über den Dienst',
        paragraphs: [
          'Streamko ist ein abonnementbasierter Online-Dienst, der Hintergrundmusik-Streaming und Audio-Management-Werkzeuge für Geschäftsumgebungen wie Cafés, Restaurants, Einzelhandelsgeschäfte, Salons, Büros und andere Gewerberäume bereitstellt.',
          'Der Dienst ermöglicht es Nutzern, lizenzierte Hintergrundmusik abzuspielen, Wiedergabeszenarien zu erstellen, Playlists zu verwalten und Audio-Durchsagen sowie Werbebotschaften über eine webbasierte Oberfläche zu planen.',
          'Streamko wird von der Eva Media LLC, Kyjiw, Ukraine, betrieben.',
        ],
      },
      {
        id: 'user-accounts',
        heading: 'Nutzerkonten',
        paragraphs: [
          'Für die Nutzung bestimmter Funktionen des Dienstes kann die Erstellung eines Kontos erforderlich sein.',
          'Nutzer sind für die Geheimhaltung ihrer Zugangsdaten sowie für alle Aktivitäten verantwortlich, die über ihr Konto erfolgen.',
          'Streamko behält sich das Recht vor, Konten zu sperren oder zu kündigen, die gegen diese Bedingungen verstoßen oder den Dienst missbräuchlich nutzen.',
        ],
      },
      {
        id: 'subscription-and-billing',
        heading: 'Abonnement und Abrechnung',
        paragraphs: [
          'Streamko wird im Abonnementmodell betrieben.',
          'Der Zugang zu bestimmten Funktionen des Dienstes kann ein aktives kostenpflichtiges Abonnement erfordern. Abonnementpläne und Preise können je nach bereitgestellten Funktionen variieren.',
          'Abonnements können wiederkehrend auf monatlicher Basis abgerechnet werden.',
          'Zahlungen für Abonnements können von externen Zahlungsdienstleistern verarbeitet werden.',
          'Nutzer können ihr Abonnement jederzeit über ihre Kontoeinstellungen kündigen. Die Kündigung wird zum Ende des laufenden Abrechnungszeitraums wirksam; bis dahin bleibt der Dienst verfügbar.',
        ],
      },
      {
        id: 'service-delivery',
        heading: 'Leistungserbringung, Zahlungsmethoden und Zugang',
        paragraphs: [
          'Streamko ist ein digitaler Abonnementdienst. Es werden keine physischen Waren geliefert.',
          'Der Zugang zum Dienst wird nach erfolgreicher Registrierung und Aktivierung des entsprechenden Abonnementplans elektronisch über eine webbasierte Oberfläche bereitgestellt.',
          'Ein kostenloser Testzugang wird, sofern verfügbar, unmittelbar nach der Registrierung aktiviert.',
          'Kostenpflichtige Abonnements werden nach erfolgreicher Zahlungsbestätigung aktiviert.',
          'Der Dienst steht Nutzern während des aktiven Abonnementzeitraums zur Verfügung.',
          'Folgende Zahlungsmethoden können verfügbar sein:',
        ],
        bullets: [
          'Zahlungskarten, verarbeitet über WayForPay.',
          'Weitere Zahlungsmethoden, die vom Zahlungsdienstleister unterstützt und beim Bezahlvorgang angezeigt werden.',
        ],
        paragraphsAfter: [
          'Abonnementpläne, Abrechnungszeiträume und anfallende Gebühren werden auf der Preisseite der Website angezeigt.',
        ],
      },
      {
        id: 'refund-policy',
        heading: 'Rückerstattung und Kündigung',
        paragraphs: [
          'Nutzer können die automatische Verlängerung ihres Abonnements jederzeit über ihre Kontoeinstellungen kündigen.',
          'Die Kündigung eines Monatsabonnements wird zum Ende des laufenden bezahlten Abrechnungszeitraums wirksam. Zahlungen für einen laufenden monatlichen Abrechnungszeitraum sind nicht erstattungsfähig, sofern das anwendbare Recht nichts anderes vorschreibt.',
          'Bei Jahresabonnements können Nutzer eine anteilige Rückerstattung für den nicht genutzten Teil des Abonnementzeitraums beantragen.',
          'Der Erstattungsbetrag wird auf Grundlage des geltenden monatlichen Abonnementtarifs für den bereits genutzten Zeitraum berechnet und nicht durch Division der Jahresgebühr durch zwölf ermittelt.',
          'Beispiel: Kauft ein Nutzer ein Jahresabonnement und beantragt die Kündigung nach drei Monaten Nutzung, kann Streamko die Kosten von drei Monaten zum geltenden Monatstarif einbehalten. Der verbleibende Betrag kann dem Nutzer erstattet werden.',
          'Erstattungsanträge werden individuell geprüft. Für einen Erstattungsantrag wenden sich Nutzer bitte unter Angabe der betreffenden Transaktion an Streamko unter info@streamko.pro.',
          'Erstattungsanträge für Jahresabonnements müssen innerhalb von 30 Kalendertagen ab dem Wirksamkeitsdatum der Kündigung des Abonnements eingereicht werden. Später eingereichte Anträge sind gegebenenfalls nicht erstattungsfähig.',
          'Genehmigte Erstattungen werden über die ursprüngliche Zahlungsmethode innerhalb eines angemessenen Zeitraums und gemäß den Richtlinien des jeweiligen Zahlungsdienstleisters abgewickelt.',
        ],
      },
      {
        id: 'account-deletion',
        heading: 'Löschung des Kontos und der Nutzerdaten',
        paragraphs: [
          'Der Nutzer kann sein Streamko-Konto und die damit verbundenen personenbezogenen Daten jederzeit löschen.',
          'Dazu muss der Nutzer:',
        ],
        steps: [
          'sich in seinem Streamko-Konto anmelden;',
          'den Bereich „Profil“ öffnen;',
          'auf „Konto löschen“ tippen;',
          'die Löschung bestätigen.',
        ],
        paragraphsAfter: [
          'Nach der Bestätigung wird das Konto gelöscht, und die damit verbundenen personenbezogenen Daten werden gelöscht oder anonymisiert, mit Ausnahme von Informationen, die Streamko nach anwendbarem Recht für einen bestimmten Zeitraum aufbewahren muss, einschließlich steuerlicher, buchhalterischer, vertraglicher oder sonstiger gesetzlicher Verpflichtungen.',
          'Die Kontolöschung ist unwiderruflich und kann zum Verlust des Zugriffs auf Einstellungen, erstellte Szenarien, Playlists, den Nutzungsverlauf und andere kontobezogene Daten führen.',
        ],
      },
      {
        id: 'intellectual-property',
        heading: 'Geistiges Eigentum',
        paragraphs: [
          'Alle über den Streamko-Dienst verfügbaren Inhalte, einschließlich Musik, Playlists, Audiomaterialien und Softwarekomponenten, sind durch Gesetze zum Schutz des geistigen Eigentums geschützt.',
          'Nutzern wird ein beschränktes, nicht ausschließliches, nicht übertragbares Recht eingeräumt, auf den Dienst zuzugreifen und ihn für ihre geschäftlichen Zwecke gemäß diesen Bedingungen zu nutzen.',
          'Nutzer dürfen über den Dienst bereitgestellte Inhalte außerhalb der Funktionalität der Plattform nicht vervielfältigen, weiterverbreiten oder anderweitig verwerten.',
        ],
      },
      {
        id: 'service-availability',
        heading: 'Verfügbarkeit des Dienstes',
        paragraphs: [
          'Streamko ist bestrebt, einen zuverlässigen und ununterbrochenen Zugang zum Dienst bereitzustellen. Der Dienst kann jedoch gelegentlich aufgrund von Wartungsarbeiten, Aktualisierungen oder technischen Problemen nicht verfügbar sein.',
          'Der Dienst wird „wie besehen“ (as is) und „wie verfügbar“ (as available) bereitgestellt.',
        ],
      },
      {
        id: 'limitation-of-liability',
        heading: 'Haftungsbeschränkung',
        paragraphs: [
          'Soweit nach anwendbarem Recht zulässig, haften Streamko und die Eva Media LLC nicht für indirekte, zufällige oder Folgeschäden, die aus der Nutzung oder der Unmöglichkeit der Nutzung des Dienstes entstehen.',
        ],
      },
      {
        id: 'changes-to-terms',
        heading: 'Änderungen des Dienstes oder der Bedingungen',
        paragraphs: [
          'Streamko kann diese Bedingungen von Zeit zu Zeit aktualisieren oder ändern. Aktualisierte Fassungen werden auf dieser Seite veröffentlicht.',
          'Die fortgesetzte Nutzung des Dienstes nach solchen Änderungen gilt als Zustimmung zu den aktualisierten Bedingungen.',
        ],
      },
      {
        id: 'contact',
        heading: 'Kontaktinformationen',
        paragraphs: [
          'Bei Fragen zu diesen Bedingungen erreichen Sie uns unter:',
        ],
        contact: {
          name: 'Eva Media LLC',
          lines: [
            'Staatliche Registrierungsnummer 42394828',
            'Pryrichna Str., 19B, #83, 04213 Kyjiw, Ukraine',
            'E-Mail: info@streamko.pro',
          ],
        },
      },
    ],
  },
};

export const PRIVACY: Record<LegalLocale, LegalDoc> = {
  en: {
    title: 'Privacy Policy',
    updated: '12 June 2026',
    sections: [
      {
        id: 'introduction',
        heading: 'Introduction',
        paragraphs: [
          'This Privacy Policy explains how Streamko collects, uses, and protects personal information when you use the Streamko application, website, and services.  Streamko is a business audio streaming service for playing licensed background music and managing audio content in commercial locations such as stores, cafés, restaurants, offices, and other business environments.  Streamko is operated by Eva Media LLC, Kyiv, Ukraine.  An account is required to use the Streamko service.  By using the Streamko application, website, or services, you agree to the collection and use of information in accordance with this Privacy Policy.',
        ],
      },
      {
        id: 'information-we-collect',
        heading: 'Information We Collect',
        paragraphs: ['We may collect the following types of information.'],
      },
      {
        id: 'account-information',
        heading: 'Account information',
        paragraphs: [
          'When you create an account, we may collect information such as name, email address, phone number, login credentials, and account and profile information.',
        ],
      },
      {
        id: 'business-information',
        heading: 'Business information',
        paragraphs: [
          'We may collect information about your business location, company name, playlists, schedules, announcements, and other information necessary for providing the service.',
        ],
      },
      {
        id: 'usage-data',
        heading: 'Usage data',
        paragraphs: [
          'We may collect information about how the service and application are used, including device information, IP address, browser type, app usage and interactions, playback activity, and log data.  We may also use cookies or similar technologies on our website to improve functionality and analyze service usage.',
        ],
      },
      {
        id: 'diagnostics',
        heading: 'Diagnostics and technical data',
        paragraphs: [
          'We may collect crash logs, performance data, and technical diagnostic information to improve the stability and performance of the application.  We do not collect precise location data, contacts, photos, health data, or other sensitive personal information.',
        ],
      },
      {
        id: 'how-we-use-information',
        heading: 'How We Use Information',
        paragraphs: [
          'We use the collected information to provide and operate the Streamko service, manage user accounts and subscriptions, enable music playback, playlists, schedules, and announcements, improve the functionality and performance of the service, communicate with users about updates, service changes, or support requests, provide customer support, maintain security and prevent unauthorized access, and comply with legal obligations.  We do not sell personal data and we do not use personal data for advertising purposes.',
        ],
      },
      {
        id: 'payment-processing',
        heading: 'Payment Processing',
        paragraphs: [
          'Payments for subscriptions may be processed by third-party payment providers.  Streamko does not store full payment card details. Payment data is handled directly by the payment provider in accordance with their own privacy and security policies.',
        ],
      },
      {
        id: 'data-sharing',
        heading: 'Data Sharing',
        paragraphs: [
          'We do not sell personal data. We may share information with trusted service providers that help us operate the platform, such as hosting providers, payment processors, analytics providers, infrastructure and technical service providers. These providers are required to handle data securely and only for the purpose of providing their services.',
        ],
      },
      {
        id: 'data-security',
        heading: 'Data Security',
        paragraphs: [
          'We take reasonable technical and organizational measures to protect personal information from unauthorized access, loss, misuse, or alteration. However, no method of transmission over the internet or electronic storage can be guaranteed to be completely secure.',
        ],
      },
      {
        id: 'data-retention',
        heading: 'Data Retention',
        paragraphs: [
          'We retain personal information only for as long as necessary to provide the service, comply with legal obligations, resolve disputes, and enforce our agreements.',
        ],
      },
      {
        id: 'user-rights',
        heading: 'User Rights',
        paragraphs: [
          'Depending on applicable law, users may have the right to access their personal data, correct or update their personal data, request deletion of their personal data, restrict processing of their data, object to data processing, and request data portability.',
          'Requests regarding personal data can be sent to the contact email provided below.',
        ],
      },
      {
        id: 'account-and-data-deletion',
        heading: 'Account and Data Deletion',
        paragraphs: [
          'Users may request deletion of their Streamko account and associated data by contacting us via email. We will process account deletion requests within a reasonable timeframe, unless we are required to retain certain information for legal or accounting purposes.',
        ],
      },
      {
        id: 'changes-to-policy',
        heading: 'Changes to this Privacy Policy',
        paragraphs: [
          'We may update this Privacy Policy from time to time. The updated version will be published on this page with a revised “Last updated” date.',
        ],
      },
      {
        id: 'contact',
        heading: 'Contact',
        paragraphs: ['If you have any questions about this Privacy Policy, please contact us:'],
        contact: {
          name: 'Eva Media LLC',
          lines: [
            'State Registration ID No. 42394828',
            'Pryrichna Str., 19B, #83, 04213 Kyiv, Ukraine',
            'Email: info@streamko.pro',
            '+380 67 735 11 51',
          ],
        },
      },
    ],
  },
  uk: {
    title: 'Політика конфіденційності',
    updated: '12 червня 2026',
    sections: [
      {
        id: 'introduction',
        heading: 'Вступ',
        paragraphs: [
          'Ця Політика конфіденційності пояснює, як Streamko збирає, використовує та захищає персональну інформацію, коли ви користуєтеся застосунком, вебсайтом і сервісами Streamko.  Streamko — це сервіс трансляції аудіо для бізнесу, призначений для відтворення ліцензованої фонової музики та керування аудіоконтентом у комерційних локаціях, таких як магазини, кафе, ресторани, офіси та інші бізнес-середовища.  Сервіс Streamko керується компанією Eva Media LLC, Київ, Україна.  Для користування сервісом Streamko потрібен обліковий запис.  Користуючись застосунком, вебсайтом або сервісами Streamko, ви погоджуєтеся на збір і використання інформації відповідно до цієї Політики конфіденційності.',
        ],
      },
      {
        id: 'information-we-collect',
        heading: 'Інформація, яку ми збираємо',
        paragraphs: ['Ми можемо збирати такі типи інформації.'],
      },
      {
        id: 'account-information',
        heading: 'Інформація облікового запису',
        paragraphs: [
          'Коли ви створюєте обліковий запис, ми можемо збирати таку інформацію, як ім’я, адреса електронної пошти, номер телефону, облікові дані для входу, а також інформацію про обліковий запис і профіль.',
        ],
      },
      {
        id: 'business-information',
        heading: 'Інформація про бізнес',
        paragraphs: [
          'Ми можемо збирати інформацію про місцезнаходження вашого бізнесу, назву компанії, плейлисти, розклади, оголошення та іншу інформацію, необхідну для надання сервісу.',
        ],
      },
      {
        id: 'usage-data',
        heading: 'Дані про використання',
        paragraphs: [
          'Ми можемо збирати інформацію про те, як використовуються сервіс і застосунок, зокрема інформацію про пристрій, IP-адресу, тип браузера, використання застосунку та взаємодію з ним, активність відтворення та дані журналів.  Ми також можемо використовувати файли cookie або подібні технології на нашому вебсайті для покращення функціональності та аналізу використання сервісу.',
        ],
      },
      {
        id: 'diagnostics',
        heading: 'Діагностика та технічні дані',
        paragraphs: [
          'Ми можемо збирати журнали збоїв, дані про продуктивність і технічну діагностичну інформацію для покращення стабільності та продуктивності застосунку.  Ми не збираємо дані про точне місцезнаходження, контакти, фотографії, дані про здоров’я чи іншу конфіденційну персональну інформацію.',
        ],
      },
      {
        id: 'how-we-use-information',
        heading: 'Як ми використовуємо інформацію',
        paragraphs: [
          'Ми використовуємо зібрану інформацію, щоб надавати та забезпечувати роботу сервісу Streamko, керувати обліковими записами користувачів і передплатами, забезпечувати відтворення музики, плейлисти, розклади та оголошення, покращувати функціональність і продуктивність сервісу, спілкуватися з користувачами щодо оновлень, змін у сервісі чи запитів до служби підтримки, надавати підтримку клієнтам, підтримувати безпеку та запобігати несанкціонованому доступу, а також виконувати юридичні зобов’язання.  Ми не продаємо персональні дані та не використовуємо їх для рекламних цілей.',
        ],
      },
      {
        id: 'payment-processing',
        heading: 'Обробка платежів',
        paragraphs: [
          'Платежі за передплату можуть оброблятися сторонніми платіжними провайдерами.  Streamko не зберігає повні дані платіжних карток. Платіжні дані обробляються безпосередньо платіжним провайдером відповідно до його власних політик конфіденційності та безпеки.',
        ],
      },
      {
        id: 'data-sharing',
        heading: 'Передача даних',
        paragraphs: [
          'Ми не продаємо персональні дані. Ми можемо передавати інформацію надійним постачальникам послуг, які допомагають нам забезпечувати роботу платформи, таким як провайдери хостингу, платіжні системи, постачальники аналітики, інфраструктурні та технічні постачальники послуг. Ці постачальники зобов’язані обробляти дані безпечно та виключно з метою надання своїх послуг.',
        ],
      },
      {
        id: 'data-security',
        heading: 'Безпека даних',
        paragraphs: [
          'Ми вживаємо обґрунтованих технічних та організаційних заходів для захисту персональної інформації від несанкціонованого доступу, втрати, неправомірного використання або зміни. Проте жоден метод передавання через інтернет чи електронного зберігання не може бути гарантовано повністю безпечним.',
        ],
      },
      {
        id: 'data-retention',
        heading: 'Зберігання даних',
        paragraphs: [
          'Ми зберігаємо персональну інформацію лише протягом часу, необхідного для надання сервісу, виконання юридичних зобов’язань, вирішення спорів і забезпечення виконання наших угод.',
        ],
      },
      {
        id: 'user-rights',
        heading: 'Права користувачів',
        paragraphs: [
          'Залежно від чинного законодавства, користувачі можуть мати право на доступ до своїх персональних даних, виправлення або оновлення своїх персональних даних, запит на видалення своїх персональних даних, обмеження обробки своїх даних, заперечення проти обробки даних, а також на перенесення даних.',
          'Запити щодо персональних даних можна надсилати на контактну електронну адресу, зазначену нижче.',
        ],
      },
      {
        id: 'account-and-data-deletion',
        heading: 'Видалення облікового запису та даних',
        paragraphs: [
          'Користувачі можуть надіслати запит на видалення свого облікового запису Streamko та пов’язаних із ним даних, звернувшись до нас електронною поштою. Ми обробляємо запити на видалення облікового запису в розумні строки, якщо ми не зобов’язані зберігати певну інформацію з юридичних або бухгалтерських причин.',
        ],
      },
      {
        id: 'changes-to-policy',
        heading: 'Зміни до цієї Політики конфіденційності',
        paragraphs: [
          'Ми можемо час від часу оновлювати цю Політику конфіденційності. Оновлена версія публікуватиметься на цій сторінці з оновленою датою «Останнє оновлення».',
        ],
      },
      {
        id: 'contact',
        heading: 'Контакти',
        paragraphs: ['Якщо у вас виникли запитання щодо цієї Політики конфіденційності, будь ласка, зв’яжіться з нами:'],
        contact: {
          name: 'Eva Media LLC',
          lines: [
            'Код ЄДРПОУ 42394828',
            'вул. Прирічна, 19Б, кв. 83, 04213 Київ, Україна',
            'Email: info@streamko.pro',
            '+380 67 735 11 51',
          ],
        },
      },
    ],
  },
  de: {
    title: 'Datenschutzerklärung',
    updated: '12. Juni 2026',
    sections: [
      {
        id: 'introduction',
        heading: 'Einleitung',
        paragraphs: [
          'Diese Datenschutzerklärung erläutert, wie Streamko personenbezogene Daten erhebt, verwendet und schützt, wenn Sie die Streamko-Anwendung, -Website und -Dienste nutzen.  Streamko ist ein Business-Audio-Streaming-Dienst zum Abspielen lizenzierter Hintergrundmusik und zur Verwaltung von Audioinhalten in gewerblichen Standorten wie Geschäften, Cafés, Restaurants, Büros und anderen Geschäftsumgebungen.  Streamko wird von der Eva Media LLC, Kyjiw, Ukraine, betrieben.  Für die Nutzung des Streamko-Dienstes ist ein Konto erforderlich.  Durch die Nutzung der Streamko-Anwendung, -Website oder -Dienste erklären Sie sich mit der Erhebung und Verwendung von Informationen gemäß dieser Datenschutzerklärung einverstanden.',
        ],
      },
      {
        id: 'information-we-collect',
        heading: 'Welche Informationen wir erheben',
        paragraphs: ['Wir können die folgenden Arten von Informationen erheben.'],
      },
      {
        id: 'account-information',
        heading: 'Kontoinformationen',
        paragraphs: [
          'Wenn Sie ein Konto erstellen, können wir Informationen wie Name, E-Mail-Adresse, Telefonnummer, Anmeldedaten sowie Konto- und Profilinformationen erheben.',
        ],
      },
      {
        id: 'business-information',
        heading: 'Geschäftsinformationen',
        paragraphs: [
          'Wir können Informationen über Ihren Geschäftsstandort, den Firmennamen, Playlists, Zeitpläne, Durchsagen und andere für die Bereitstellung des Dienstes erforderliche Informationen erheben.',
        ],
      },
      {
        id: 'usage-data',
        heading: 'Nutzungsdaten',
        paragraphs: [
          'Wir können Informationen darüber erheben, wie der Dienst und die Anwendung genutzt werden, einschließlich Geräteinformationen, IP-Adresse, Browsertyp, App-Nutzung und -Interaktionen, Wiedergabeaktivität und Protokolldaten.  Wir können außerdem Cookies oder ähnliche Technologien auf unserer Website einsetzen, um die Funktionalität zu verbessern und die Nutzung des Dienstes zu analysieren.',
        ],
      },
      {
        id: 'diagnostics',
        heading: 'Diagnose- und technische Daten',
        paragraphs: [
          'Wir können Absturzprotokolle, Leistungsdaten und technische Diagnoseinformationen erheben, um die Stabilität und Leistung der Anwendung zu verbessern.  Wir erheben keine genauen Standortdaten, Kontakte, Fotos, Gesundheitsdaten oder andere sensible personenbezogene Daten.',
        ],
      },
      {
        id: 'how-we-use-information',
        heading: 'Wie wir Informationen verwenden',
        paragraphs: [
          'Wir verwenden die erhobenen Informationen, um den Streamko-Dienst bereitzustellen und zu betreiben, Nutzerkonten und Abonnements zu verwalten, Musikwiedergabe, Playlists, Zeitpläne und Durchsagen zu ermöglichen, die Funktionalität und Leistung des Dienstes zu verbessern, mit Nutzern über Aktualisierungen, Änderungen am Dienst oder Supportanfragen zu kommunizieren, Kundensupport zu leisten, die Sicherheit aufrechtzuerhalten und unbefugten Zugriff zu verhindern sowie gesetzliche Verpflichtungen zu erfüllen.  Wir verkaufen keine personenbezogenen Daten und verwenden personenbezogene Daten nicht zu Werbezwecken.',
        ],
      },
      {
        id: 'payment-processing',
        heading: 'Zahlungsabwicklung',
        paragraphs: [
          'Zahlungen für Abonnements können von externen Zahlungsdienstleistern verarbeitet werden.  Streamko speichert keine vollständigen Zahlungskartendaten. Zahlungsdaten werden direkt vom Zahlungsdienstleister gemäß dessen eigenen Datenschutz- und Sicherheitsrichtlinien verarbeitet.',
        ],
      },
      {
        id: 'data-sharing',
        heading: 'Weitergabe von Daten',
        paragraphs: [
          'Wir verkaufen keine personenbezogenen Daten. Wir können Informationen an vertrauenswürdige Dienstleister weitergeben, die uns beim Betrieb der Plattform unterstützen, etwa Hosting-Anbieter, Zahlungsdienstleister, Analyseanbieter sowie Infrastruktur- und technische Dienstleister. Diese Dienstleister sind verpflichtet, Daten sicher und ausschließlich zum Zweck der Erbringung ihrer Leistungen zu verarbeiten.',
        ],
      },
      {
        id: 'data-security',
        heading: 'Datensicherheit',
        paragraphs: [
          'Wir treffen angemessene technische und organisatorische Maßnahmen, um personenbezogene Daten vor unbefugtem Zugriff, Verlust, Missbrauch oder Veränderung zu schützen. Keine Methode der Übertragung über das Internet oder der elektronischen Speicherung kann jedoch als vollständig sicher garantiert werden.',
        ],
      },
      {
        id: 'data-retention',
        heading: 'Datenspeicherung',
        paragraphs: [
          'Wir speichern personenbezogene Daten nur so lange, wie es für die Bereitstellung des Dienstes, die Erfüllung gesetzlicher Verpflichtungen, die Beilegung von Streitigkeiten und die Durchsetzung unserer Vereinbarungen erforderlich ist.',
        ],
      },
      {
        id: 'user-rights',
        heading: 'Rechte der Nutzer',
        paragraphs: [
          'Je nach anwendbarem Recht können Nutzer das Recht haben, auf ihre personenbezogenen Daten zuzugreifen, sie zu berichtigen oder zu aktualisieren, ihre Löschung zu verlangen, die Verarbeitung ihrer Daten einzuschränken, der Datenverarbeitung zu widersprechen und die Datenübertragbarkeit zu verlangen.',
          'Anfragen zu personenbezogenen Daten können an die unten angegebene Kontakt-E-Mail-Adresse gesendet werden.',
        ],
      },
      {
        id: 'account-and-data-deletion',
        heading: 'Löschung von Konto und Daten',
        paragraphs: [
          'Nutzer können die Löschung ihres Streamko-Kontos und der damit verbundenen Daten per E-Mail bei uns beantragen. Wir bearbeiten Löschanträge innerhalb eines angemessenen Zeitraums, sofern wir nicht verpflichtet sind, bestimmte Informationen aus rechtlichen oder buchhalterischen Gründen aufzubewahren.',
        ],
      },
      {
        id: 'changes-to-policy',
        heading: 'Änderungen dieser Datenschutzerklärung',
        paragraphs: [
          'Wir können diese Datenschutzerklärung von Zeit zu Zeit aktualisieren. Die aktualisierte Fassung wird auf dieser Seite mit einem angepassten Datum („Zuletzt aktualisiert“) veröffentlicht.',
        ],
      },
      {
        id: 'contact',
        heading: 'Kontakt',
        paragraphs: ['Bei Fragen zu dieser Datenschutzerklärung kontaktieren Sie uns bitte:'],
        contact: {
          name: 'Eva Media LLC',
          lines: [
            'Staatliche Registrierungsnummer 42394828',
            'Pryrichna Str., 19B, #83, 04213 Kyjiw, Ukraine',
            'E-Mail: info@streamko.pro',
            '+380 67 735 11 51',
          ],
        },
      },
    ],
  },
};

/** Standalone Contacts page — the company's contact details (linked from footer). */
export const CONTACTS: Record<LegalLocale, ContactPageContent> = {
  en: {
    title: 'Contacts',
    intro: 'Have a question about Streamko, your subscription, or billing? Get in touch with the team behind the service.',
    orgName: 'Eva Media LLC',
    registration: 'State Registration ID No. 42394828',
    address: 'Pryrichna Str., 19B, #83, 04213 Kyiv, Ukraine',
    email: 'info@streamko.pro',
    phone: '+380 67 735 11 51',
  },
  uk: {
    title: 'Контакти',
    intro: 'Маєте запитання про Streamko, вашу передплату чи оплату? Зв’яжіться з командою, що стоїть за сервісом.',
    orgName: 'ТОВ «ЄВА МЕДІА»',
    registration: 'Код ЄДРПОУ 42394828',
    address: 'вул. Прирічна, 19Б, офіс 83, м. Київ, 04213, Україна',
    email: 'info@streamko.pro',
    phone: '+380 67 735 11 51',
  },
  de: {
    title: 'Kontakt',
    intro: 'Haben Sie eine Frage zu Streamko, Ihrem Abonnement oder zur Abrechnung? Kontaktieren Sie das Team hinter dem Dienst.',
    orgName: 'Eva Media LLC',
    registration: 'Staatliche Registrierungsnummer 42394828',
    address: 'Pryrichna Str., 19B, #83, 04213 Kyjiw, Ukraine',
    email: 'info@streamko.pro',
    phone: '+380 67 735 11 51',
  },
};
